import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Plug, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Unplug, Copy, ArrowUpRight,
  MessagesSquare, CheckSquare, Hash, CalendarDays, FolderOpen, Mail, Figma, Newspaper,
  BookOpen, Webhook, Workflow, Server, Link2, Sparkles, Send,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { useAuth } from "@/context/AuthContext";

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50";
const primary = `${btn} bg-indigo-600 hover:bg-indigo-700 text-indigo-50`;
const ghost = `${btn} border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50`;

// What each Bloom Connect tool group would do inside COMO. Shown as the roadmap under the connected card.
const BLOOM_USES = [
  ["Reply in private", "Move a heated comment thread to a DM or WhatsApp conversation with an AI agent."],
  ["Answer open questions", "Push questions people keep asking in comments into the Bloom Connect knowledge base."],
  ["Reach supporters", "Send a broadcast (town hall, update) to contact lists, timed with the Content Studio calendar."],
  ["Follow up", "Create contacts, tasks and appointments from comments that need a human."],
];

// Placeholders: nothing here is connected yet. `via` says how it would plug in.
const PLACEHOLDERS = [
  { group: "Team work", items: [
    { icon: CheckSquare, name: "ClickUp", desc: "Turn a Recommended next step into a task with owner and due date." },
    { icon: Hash, name: "Slack", desc: "Post crisis alerts and the daily brand-health summary to a channel." },
    { icon: BookOpen, name: "Notion", desc: "Keep the weekly report and content calendar in your workspace." },
  ]},
  { group: "Calendar and files", items: [
    { icon: CalendarDays, name: "Google Calendar", desc: "Put scheduled posts and town halls on the team calendar." },
    { icon: FolderOpen, name: "Google Drive", desc: "Save weekly reports and post-mortems to a shared folder." },
    { icon: Mail, name: "Gmail", desc: "Email the weekly digest to the politician and staff." },
  ]},
  { group: "Creative and research", items: [
    { icon: Figma, name: "Figma / Canva", desc: "Pull campaign visuals into post drafts." },
    { icon: Newspaper, name: "News monitor", desc: "Let content ideas react to what is in the news today." },
  ]},
  { group: "Automation", items: [
    { icon: Workflow, name: "Zapier / Make", desc: "Catch-all for tools without an MCP server." },
    { icon: Webhook, name: "Webhooks", desc: "Send COMO events (new alert, post published) to any URL." },
  ]},
];

function Pill({ tone, children }) {
  const t = {
    ok: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    warn: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    bad: "bg-red-500/10 text-red-600 border-red-500/20",
    off: "bg-slate-500/10 text-slate-500 border-slate-500/20",
  }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${t}`}>{children}</span>;
}

function BloomConnectCard({ state, serverUrl, qs, canWrite, reload }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const connected = state?.connected;
  const broken = state?.status === "error";

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const connect = (e) => {
    e.preventDefault();
    run(async () => { await api.put(`/integrations/bloom-connect${qs}`, { api_key: key.trim() }); setKey(""); }, "Bloom Connect reached");
  };
  const mcpCmd = `claude mcp add --transport http bloom-connect ${serverUrl} --header "X-API-Key: YOUR_API_KEY"`;

  return (
    <section className="bg-card rounded-2xl border border-slate-100 shadow-card p-6 space-y-5" data-testid="int-bloom-connect">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl border border-slate-100 flex items-center justify-center text-indigo-600"><MessagesSquare className="w-5 h-5" strokeWidth={1.5} /></div>
          <div>
            <h2 className="font-heading text-xl text-slate-900">Bloom Connect</h2>
            <p className="text-sm text-slate-500">AI messaging for WhatsApp, Messenger, Instagram DMs and SMS. Every feature is available to COMO through MCP.</p>
          </div>
        </div>
        {broken ? <Pill tone="bad">Unreachable</Pill> : connected ? <Pill tone="ok">Reachable</Pill> : <Pill tone="off">Not connected</Pill>}
      </div>

      {connected || broken ? (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl border border-slate-100 p-3"><p className="text-xs text-slate-400">Tools available</p><p className="font-heading text-2xl text-slate-900" data-testid="bc-tool-count">{state.tool_count}</p></div>
            <div className="rounded-xl border border-slate-100 p-3"><p className="text-xs text-slate-400">API key</p><p className="font-mono text-slate-700">{state.key_hint}</p></div>
            <div className="rounded-xl border border-slate-100 p-3"><p className="text-xs text-slate-400">Last checked</p><p className="text-slate-700">{state.last_tested_at ? new Date(state.last_tested_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "-"}</p></div>
          </div>
          {state.categories?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {state.categories.map((c) => <span key={c.name} className="text-xs px-2 py-1 rounded-md bg-slate-500/10 text-slate-500">{c.name} <span className="font-mono">{c.count}</span></span>)}
            </div>
          )}
          {broken && <p className="text-sm text-red-600 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{state.error}</p>}
          {!state.key_verified && !broken && (
            <p className="text-xs text-slate-500 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 text-amber-500" />
              Bloom Connect lists its tools without checking the key, so COMO can only confirm the server is reachable. The key is checked the first time COMO uses it for something, and a wrong key will show up then.</p>
          )}
          {canWrite && (
            <div className="flex gap-2 flex-wrap">
              <button className={ghost} disabled={busy} onClick={() => run(() => api.post(`/integrations/bloom-connect/test${qs}`), "Checked")} data-testid="bc-test">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}Test connection</button>
              <button className={ghost} disabled={busy} onClick={() => run(() => api.delete(`/integrations/bloom-connect${qs}`), "Disconnected")} data-testid="bc-disconnect"><Unplug className="w-3.5 h-3.5" />Disconnect</button>
            </div>
          )}
        </div>
      ) : canWrite ? (
        <form onSubmit={connect} className="space-y-2" data-testid="bc-form">
          <label className="text-xs font-semibold text-slate-500 block">Bloom Connect API key
            <input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Settings > Integrations > API Key in Bloom Connect"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-input bg-card text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/30" data-testid="bc-key" />
          </label>
          <div className="flex items-center gap-3 flex-wrap">
            <button className={primary} disabled={busy || key.trim().length < 8} data-testid="bc-connect">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}Connect</button>
            <span className="text-xs text-slate-400">The key is encrypted and never shown again. Needs a Bloom Connect plan with API access.</span>
          </div>
        </form>
      ) : <p className="text-sm text-slate-500">Ask a Client Admin to connect Bloom Connect.</p>}

      <div className="pt-4 border-t border-slate-100">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">What COMO will do with it</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {BLOOM_USES.map(([t, d]) => (
            <div key={t} className="rounded-xl border border-dashed border-slate-200 p-3">
              <p className="text-sm font-medium text-slate-700 flex items-center gap-1.5">{t} <span className="text-[10px] uppercase tracking-wide text-slate-400">Planned</span></p>
              <p className="text-xs text-slate-500 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </div>

      <details className="text-sm text-slate-500">
        <summary className="cursor-pointer text-slate-600 font-medium">Use Bloom Connect from your own AI tools too</summary>
        <div className="mt-2 space-y-2">
          <p>Server: <span className="font-mono text-xs">{serverUrl}</span> (header <span className="font-mono text-xs">X-API-Key</span>)</p>
          <div className="flex items-start gap-2"><code className="flex-1 text-xs bg-slate-500/10 rounded-lg p-2 break-all">{mcpCmd}</code>
            <button className={ghost} onClick={() => navigator.clipboard?.writeText(mcpCmd).then(() => toast.success("Copied"))} aria-label="Copy command"><Copy className="w-3.5 h-3.5" /></button></div>
          <a className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700" href="https://docs.connect.bybloomlabs.com/integrations/connect-ai-clients/" target="_blank" rel="noreferrer">Bloom Connect docs <ArrowUpRight className="w-3.5 h-3.5" /></a>
        </div>
      </details>
    </section>
  );
}

function CoreRow({ data }) {
  const pub = { ok: "Publishing on", sandbox: "Publishing simulated", needs_reauth: "Reconnect for publishing", unknown: "Publishing untested", none: "No Page connected" }[data.publishing.state];
  const rows = [
    [Link2, "Facebook and Instagram", `${data.facebook.count + data.instagram.count} connected`, "/accounts", data.facebook.broken || data.instagram.broken ? "bad" : "ok"],
    [Sparkles, "AI key (OpenRouter)", data.ai.configured ? "Configured" : "Not set", "/settings", data.ai.configured ? "ok" : "warn"],
    [Send, "Post publishing", pub, "/content", data.publishing.state === "ok" ? "ok" : "warn"],
  ];
  return (
    <section data-testid="core-connections">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Core connections</h2>
      <div className="grid md:grid-cols-3 gap-3">
        {rows.map(([Icon, name, status, to, tone]) => (
          <Link key={name} to={to} className="bg-card rounded-xl border border-slate-100 p-4 flex items-center gap-3 hover:border-slate-300 transition-colors">
            <Icon className="w-4 h-4 text-slate-500 shrink-0" strokeWidth={1.5} />
            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{name}</p><p className="text-xs text-slate-500 truncate">{status}</p></div>
            {tone === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className={`w-4 h-4 ${tone === "bad" ? "text-red-500" : "text-amber-500"}`} />}
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Integrations() {
  const { user } = useAuth();
  const canWrite = user?.role !== "client_viewer";
  const { ClientPicker, scopeParam, ready } = useClientScope();
  const qs = scopeParam ? `?${scopeParam}` : "";
  const [data, setData] = useState(null);

  const load = useCallback(() => api.get(`/integrations${qs}`).then(({ data }) => setData(data)).catch(() => setData(false)), [qs]);
  useEffect(() => { if (ready) { setData(null); load(); } }, [ready, load]);

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">Administration</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Integrations</h1>
            <p className="text-sm text-slate-500 mt-1">Connect COMO to your other tools through MCP, so a next step can become a message, a task or a calendar entry.</p>
          </div>
          <ClientPicker />
        </div>

        {data === null && <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-indigo-600" /></div>}
        {data === false && <p className="text-sm text-slate-500">Could not load integrations.</p>}

        {data && (
          <>
            <BloomConnectCard state={data.mcp.bloom_connect} serverUrl={data.mcp.server_url} qs={qs} canWrite={canWrite} reload={load} />

            <section data-testid="placeholders">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">More integrations</h2>
                <span className="text-xs text-slate-400">Planned. Not connected yet.</span>
              </div>
              <div className="space-y-5">
                {PLACEHOLDERS.map((g) => (
                  <div key={g.group}>
                    <p className="text-xs text-slate-500 mb-2">{g.group}</p>
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
                      {g.items.map((it) => (
                        <div key={it.name} className="rounded-xl border border-dashed border-slate-200 p-4 flex gap-3" data-testid={`int-soon-${it.name.split(/[ /]/)[0].toLowerCase()}`}>
                          <it.icon className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                          <div><p className="text-sm font-medium text-slate-700">{it.name}</p><p className="text-xs text-slate-500 mt-0.5">{it.desc}</p></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="rounded-xl border border-dashed border-slate-200 p-4 flex gap-3" data-testid="int-soon-comoserver">
                  <Server className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div><p className="text-sm font-medium text-slate-700">COMO as an MCP server</p>
                    <p className="text-xs text-slate-500 mt-0.5">Let Claude and other assistants ask COMO for brand health, next steps and draft posts. Read-only first.</p></div>
                </div>
              </div>
            </section>

            <CoreRow data={data} />
          </>
        )}
      </div>
    </Layout>
  );
}
